import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import {
  BasicBotData,
  BotData,
  BotStatus,
  BotType,
  DisplayResultMessage,
  OpenMessage,
  WorkspaceType,
} from "@shared/types";
import { useBotStatusTag } from "@shared/features/bots/hooks";
import { BOT_TYPE_TO_USAGE_TYPE } from "@shared/features/settings/constants";
import { useContext, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import {
  AccountObjectType,
  UseQuotaModal,
} from "@shared/features/settings/types";
import {
  getBotOneClickFollowCacheKey,
  getBotsInactiveAccountMessage,
} from "@shared/features/bots/utils";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import {
  useClientsData,
  useExchangesMetadata,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc";
import { getReadOnlyAccountWarning } from "@shared/utils/misc";

interface useBotActionsProps {
  bot: BotData | BasicBotData;
  useQuotaModal: UseQuotaModal;
  openMessage: OpenMessage;
  isMobileApp?: boolean;
  displayResultMessage: DisplayResultMessage;
  navigateToCopiedFromWorkspaceForm?: () => void;
}

const useBotActions = ({
  bot,
  useQuotaModal,
  openMessage,
  isMobileApp,
  displayResultMessage,
  navigateToCopiedFromWorkspaceForm,
}: useBotActionsProps) => {
  const [bulkUpdateBotsModalOpen, setBulkUpdateBotsModalOpen] = useState(false);
  const { setIsEditMode } =
    useContext<ContextProps<BotData> | undefined>(CardsSelectorContext) || {};
  const [deleteBot, { isLoading: deleteLoading }] =
    sharedLib.sharedApi.bots.useDeleteBotMutation();
  const [setDefaultOneClickSignalsBot] =
    sharedLib.sharedApi.bots.useSetDefaultOneClickSignalsBotMutation({
      fixedCacheKey: getBotOneClickFollowCacheKey(bot),
    });
  const [modalIsActiveState, setModalIsActiveState] = useState<
    boolean | undefined
  >();
  const [actionModalOpen, setActionModalOpen] = useState(false);

  const botType = bot.bot_type;
  const { invalidAccountData } = useBotStatusTag(bot);
  const isInvalidAccount = invalidAccountData.isInvalidOrInactive;
  const isBotActive = modalIsActiveState ?? bot.status === BotStatus.ACTIVE;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const { workspace, setWorkspaceType, realUser } = useSharedWorkspace({});
  const isDemoWorkspace = workspace === WorkspaceType.DEMO;
  const { isSubUser } = useSubUserPermissions();
  const { getAccountIsReadOnly } = useClientsData();
  const { getIsFree } = useExchangesMetadata();
  const accountIsReadOnly = getAccountIsReadOnly(bot.client_id);

  const { handleTriggerClick, quotaModal } = useQuotaModal({
    usageType: BOT_TYPE_TO_USAGE_TYPE[botType],
  });

  const showBotsInactiveAccountMessage = (actionName: string) =>
    openMessage?.(
      getBotsInactiveAccountMessage(
        actionName,
        invalidAccountData.isInvalid,
        invalidAccountData.isInactive
      ),
      "warning"
    );

  const showReadOnlyAccountMessage = () => {
    openMessage?.(getReadOnlyAccountWarning(AccountObjectType.BOT), "warning");
  };

  const closeMobileModal =
    isMobileApp && actionModalOpen
      ? () => setActionModalOpen(false)
      : undefined;

  const handleOpenModal = (openModal: () => void) => {
    if (isMobileApp) {
      setActionModalOpen(false);
      setTimeout(openModal, 50);
    } else {
      openModal();
    }
  };

  const openUpdateBotsModal = (isActive: boolean) => {
    setModalIsActiveState(isActive);
    handleOpenModal(() => setBulkUpdateBotsModalOpen(true));
  };

  const handleChangeStatusClick = async () => {
    if (accountIsReadOnly) {
      showReadOnlyAccountMessage();
      return;
    }
    const isActive = bot.status === BotStatus.ACTIVE;
    const isQuotaError =
      !isActive &&
      !getIsFree(bot.exchange) &&
      (await handleTriggerClick({
        closeModal: closeMobileModal,
      }));
    if (!isQuotaError) {
      if (!isBotActive && isInvalidAccount) {
        showBotsInactiveAccountMessage("activate");
      } else {
        openUpdateBotsModal(isActive);
      }
    }
  };

  const oneClickFollowDefaultData = {
    key: "oneClickFollowDefault",
    shouldDisplay: isSignalsBot && !isDemoWorkspace && !isSubUser,
    text: bot.is_default_one_click
      ? "Remove as Telegram One-Click Follow default"
      : "Set as Telegram One-Click Follow default",
    onClick: () =>
      displayResultMessage(
        setDefaultOneClickSignalsBot({
          bot_id: bot.id,
          should_set_default: !bot.is_default_one_click,
        }).unwrap()
      ).catch(() => {}),
  };

  let onShareClickError = "";
  if (botType === BotType.ASAP && !bot.is_public) {
    onShareClickError = bot.is_copied
      ? "You can't share copied bots."
      : "You can't share bots that are set as private. Please enable the 'discoverable' toggle and try again.";
  }

  const onClickDisabledCopyBot = () => {
    if (!realUser?.has_valid_clients_connected) {
      openMessage?.(
        "You don't have any valid clients in the user workspace",
        "warning"
      );
    }
  };

  const onCopyToWorkspaceClick = () => {
    if (isDemoWorkspace && realUser?.has_valid_clients_connected) {
      setWorkspaceType(WorkspaceType.USER);
      navigateToCopiedFromWorkspaceForm?.();
    } else if (workspace === WorkspaceType.USER) {
      setWorkspaceType(WorkspaceType.DEMO);
      navigateToCopiedFromWorkspaceForm?.();
    } else {
      onClickDisabledCopyBot();
    }
  };

  return {
    isInvalidAccount,
    isBotActive,
    botType,
    deleteLoading,
    bulkUpdateBotsModalOpen,
    setBulkUpdateBotsModalOpen,
    setModalIsActiveState,
    modalIsActiveState,
    deleteBot,
    isSignalsBot,
    isTradingViewSignalsBot,
    setIsEditMode,
    showBotsInactiveAccountMessage,
    handleOpenModal,
    handleChangeStatusClick,
    actionModalOpen,
    setActionModalOpen,
    oneClickFollowDefaultData,
    onShareClickError,
    onCopyToWorkspaceClick,
    workspace,
    isTradingViewBot,
    accountIsReadOnly,
    showReadOnlyAccountMessage,
    quotaModal,
  };
};

export default useBotActions;
